import { Box, Container, Grid2 } from "@mui/material";
import { useRef } from "react";
import { Link } from "react-router-dom";

export const IntelligentClaimsDocumentSummarizationDetail = () => {
  const productSectionRef = useRef(null);
  const valueSectionRef = useRef(null);
  const financialSectionRef = useRef(null);

  const scrollToSection = (sectionRef) => {
    if (!sectionRef?.current) return;
    const sectionTop =
      sectionRef.current.getBoundingClientRect().top + window.pageYOffset;
    window.scrollTo({ top: sectionTop - 110, behavior: "smooth" });
  };

  return (
  <Container maxWidth="lg" className="pt-6">
      <Box className="mb-8 md:mb-10 rounded-xl border border-[#021744] bg-[#021744]">
        <Box className="bg-[#021744] text-white rounded-xl p-6 md:p-10">
          <Box className="w-full flex flex-wrap justify-between items-start gap-4 mb-6">
            <span className="inline-flex text-xs px-3 py-1 rounded-full bg-[#42DDD1] border border-[#42DDD1] text-[#021744]">
              Use Case Spotlight
            </span>
            <Box className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => scrollToSection(valueSectionRef)}
                className="text-xs px-3 py-1 rounded-full border border-white/40 bg-white/10 text-white hover:bg-[#42DDD1] hover:text-[#021744]"
              >
                Value
              </button>
              <button
                type="button"
                onClick={() => scrollToSection(financialSectionRef)}
                className="text-xs px-3 py-1 rounded-full border border-white/40 bg-white/10 text-white hover:bg-[#42DDD1] hover:text-[#021744]"
              >
                Financial Impact
              </button>
              <button
                type="button"
                onClick={() => scrollToSection(productSectionRef)}
                className="text-xs px-3 py-1 rounded-full border border-white/40 bg-white/10 text-white hover:bg-[#42DDD1] hover:text-[#021744]"
              >
                Product
              </button>
            </Box>
          </Box>
          <h1 className="text-left text-[26px] md:text-[34px] tracking-normal font-medium mb-4">
            Turn Document Overload into Actionable Intelligence
          </h1>
          <p className="text-[#d6e6ff] leading-7 mb-4 text-left">
            In the evolving insurance landscape, one of the biggest operational
            bottlenecks remains the manual review of claim-related
            documents—from medical records and demand letters to case
            evaluations and attorney correspondence. These documents hold
            critical insights that impact triage, reserve setting, litigation
            readiness, and settlement decisions. Yet, most teams are still
            buried under paperwork, relying on human memory and manual review to
            catch key facts.
          </p>
          <p className="text-[#d6e6ff] leading-7 text-left">
            DocuCharlee eliminates this drag. Using AI-powered text analysis, it
            scans and summarizes high-volume, unstructured documents—extracting
            actionable insights like trial dates, exposure red flags, treatment
            progress, and negotiation signals. Whether it’s a 200-page medical
            file or a complex demand package, DocuCharlee flags what matters
            most—delivering clarity, context, and confidence to frontline claims
            teams. It integrates seamlessly with existing workflows or within
            4SeeCharlee, amplifying its value across the claims lifecycle.
          </p>
        </Box>
      </Box>
      <Grid2 container spacing={2} className="mb-8 items-stretch">
        <Grid2 size={{ xs: 12, md: 4 }}>
          <Box className="rounded-xl border border-[#E6EAF2] bg-white p-5 h-full">
            <p className="text-center font-bold text-[#294b78] mb-2">Role</p>
            <p className="text-sm text-[#1a2f55]">
              Adjusters, FNOL Teams, Litigation Teams, Supervisors
            </p>
          </Box>
        </Grid2>
        <Grid2 size={{ xs: 12, md: 4 }}>
          <Box className="rounded-xl border border-[#E6EAF2] bg-white p-5 h-full">
            <p className="text-center font-bold text-[#294b78] mb-2">
              Job at Hand
            </p>
            <p className="text-sm text-[#1a2f55]">
              Quickly understand and act on large volumes of incoming claim
              documents
            </p>
          </Box>
        </Grid2>
        <Grid2 size={{ xs: 12, md: 4 }}>
          <Box className="rounded-xl border border-[#E6EAF2] bg-white p-5 h-full">
            <p className="text-center font-bold text-[#294b78] mb-2">Trigger</p>
            <p className="text-sm text-[#1a2f55]">
              Surge in medical records, demand packages, or case evaluations
              post-FNOL
            </p>
          </Box>
        </Grid2>
      </Grid2>
      <Box className="rounded-xl border border-[#E6EAF2] bg-white p-6 mb-4">
        <h3 className="text-[22px] text-[#021744] font-medium mb-3">
          Opportunity
        </h3>
        <p className="text-[#42506a]">
          Fast access to summarized, actionable content improves triage, reserve
          setting, and early mitigation planning.
        </p>
      </Box>
      <Grid2
        ref={productSectionRef}
        container
        spacing={2}
        className="mb-4 items-stretch"
      >
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Box className="rounded-xl border border-[#E6EAF2] bg-white p-6 h-full">
            <h4 className="text-[#021744] font-medium mb-2">Before Charlee</h4>
            <p className="text-[#42506a]">
              Teams manually reviewed large document sets, often missing key
              indicators buried in PDFs or lengthy attachments.
            </p>
          </Box>
        </Grid2>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Box className="rounded-xl border border-[#E6EAF2] bg-white p-6 h-full">
            <h4 className="text-[#021744] font-medium mb-2">With Charlee</h4>
            <p className="text-[#42506a]">
              DocuCharlee uses AI to summarize and extract key
              information—highlighting exposures, entities, legal milestones,
              and treatment details that drive claim strategy.
            </p>
          </Box>
        </Grid2>
      </Grid2>
      <Grid2 container spacing={2} className="mb-4 items-stretch">
        <Grid2 size={{ xs: 12, md: 4 }}>
          <Box className="rounded-xl border border-[#021744] bg-[#021744] text-white p-5 h-full">
            <p className="text-xs text-[#42DDD1] mb-2">Product Used</p>
            <p>📄 DocuCharlee</p>
          </Box>
        </Grid2>
        <Grid2 size={{ xs: 12, md: 4 }}>
          <Box className="rounded-xl border border-[#021744] bg-[#021744] text-white p-5 h-full">
            <p className="text-xs text-[#42DDD1] mb-2">LOBs</p>
            <p>All</p>
          </Box>
        </Grid2>
        <Grid2 size={{ xs: 12, md: 4 }}>
          <Box className="rounded-xl border border-[#021744] bg-[#021744] text-white p-5 h-full">
            <p className="text-xs text-[#42DDD1] mb-2">Area</p>
            <Box className="flex gap-2 flex-wrap">
              <span className="px-3 py-1 text-xs rounded-full bg-white/10">
                Claims
              </span>
              <span className="px-3 py-1 text-xs rounded-full bg-white/10">
                Documents
              </span>
              <span className="px-3 py-1 text-xs rounded-full bg-white/10">
                Demand Packages
              </span>
              <span className="px-3 py-1 text-xs rounded-full bg-white/10">
                FNOL
              </span>
            </Box>
          </Box>
        </Grid2>
      </Grid2>
      <Box
        ref={valueSectionRef}
        className="rounded-xl border border-[#E6EAF2] bg-white p-6 mb-4"
      >
        <h3 className="text-[22px] text-[#021744] font-medium mb-3">
          Value Proposition
        </h3>
        <ul className="text-[#42506a] space-y-2 text-left">
          <li>
            • DocuCharlee enhances claim handling across the full claims
            lifecycle.
          </li>
          <li>
            • Improves critical metrics: cycle time, severity, accuracy,
            compliance, and productivity.
          </li>
          <li>
            • Highlights key sections and raises alerts within the claims
            management system for real-time impact.
          </li>
        </ul>
      </Box>
      <Box
        ref={financialSectionRef}
        className="rounded-xl border border-[#E6EAF2] bg-white p-6 mb-4"
      >
        <h3 className="text-[22px] text-[#021744] font-medium mb-3">
          Financial Impact
        </h3>
        <p className="text-[#42506a] mb-2 text-left">
          Manual document review results in:
        </p>
        <ul className="text-[#42506a] space-y-2 mb-3 text-left">
          <li>
            • Delayed reserve accuracy due to missed or late-reviewed details
          </li>
          <li>
            • Increased litigation risk from missed demand packages or trial
            dates
          </li>
          <li>• High adjuster workload, limiting time for strategic tasks</li>
        </ul>
        <Box className="rounded-lg border border-[#E6EAF2] bg-[#EDF3F8] p-4 text-left">
          <p className="text-[#021744] font-bold mb-2">Why it matters</p>
          <ul className="text-[#42506a] space-y-2 text-left">
            <li>• 75% reduction in document review time</li>
            <li>• $300–$500 savings per claim in high-volume settings</li>
            <li>• Earlier detection of litigation risk and better triage</li>
            <li>
              • Reallocation of adjuster time to negotiation and resolution
            </li>
            <li>
              • When combined with 4SeeCharlee: full-cycle intelligence
              integration
            </li>
          </ul>
        </Box>
      </Box>
      <Box className="rounded-xl border border-[#E6EAF2] bg-white p-6 mb-4">
        <h3 className="text-[28px] md:text-[32px] text-[#021744] font-bold mb-4">
          Results
        </h3>
        <Grid2 container spacing={2} className="items-stretch">
          <Grid2 size={{ xs: 12, md: 3 }}>
            <Box className="rounded-lg bg-[#EDF3F8] p-4 h-full">
              <p className="text-xl md:text-2xl font-bold text-[#021744] whitespace-nowrap">
                ⏱️ 75%
              </p>
              <p className="text-[#42506a]">
                reduction in document review time
              </p>
            </Box>
          </Grid2>
          <Grid2 size={{ xs: 12, md: 3 }}>
            <Box className="rounded-lg bg-[#EDF3F8] p-4 h-full">
              <p>🔎</p>
              <p className="text-[#42506a]">
                key topics flagged for triage, litigation, or negotiation
              </p>
            </Box>
          </Grid2>
          <Grid2 size={{ xs: 12, md: 3 }}>
            <Box className="rounded-lg bg-[#EDF3F8] p-4 h-full">
              <p>💬</p>
              <p className="text-[#42506a]">
                “We’re no longer buried under paperwork—we’re acting on
                intelligence.”
              </p>
            </Box>
          </Grid2>
          <Grid2 size={{ xs: 12, md: 3 }}>
            <Box className="rounded-lg bg-[#EDF3F8] p-4 h-full">
              <p className="text-xl md:text-2xl font-bold text-[#021744] whitespace-nowrap">
                🚀 24–48h
              </p>
              <p className="text-[#42506a]">
                DocuCharlee summaries; 30–60 days with 4SeeCharlee integration
              </p>
            </Box>
          </Grid2>
        </Grid2>
      </Box>
      <Box className="rounded-xl border border-[#E6EAF2] bg-white p-6 mb-4">
        <Box className="rounded-lg border border-[#E6EAF2] bg-[#EDF3F8] p-4 text-left">
          <p className="text-[#021744] font-bold mb-2">Why It Matters</p>
          <p className="text-[#42506a]">
            Intelligent summarization improves speed, accuracy, and exposure
            awareness—turning document overload into decision-making advantage.
          </p>
        </Box>
      </Box>
      <Box className="pt-8 text-center">
        <Link
          to="/blogs/usecases"
          className="inline-flex items-center gap-2 text-[#021744] border border-[#021744] bg-white rounded-full px-6 py-3 hover:bg-[#021744] hover:text-white"
        >
          Back to Use Cases
        </Link>
      </Box>
    </Container>
  );
};
